import { Label, Switch } from "@libs/zapi-ui/shadcn";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { updateFeaturePermission } from "./updateFeaturePermission";
import { toast } from "sonner";
import { updateCustomSnippetsUrl } from "@/zapi-frontend/pages/SettingsPage/services/updateCustomSnippetsUrl";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";

type TLabsModeSwitchProps = {
  variant?: "default" | "inline";
  id?: string;
  showLabel?: boolean;
  showDescription?: boolean;
  className?: string;
};

export function LabsModeSwitch(props: TLabsModeSwitchProps) {
  const { variant = "default", id = "labs-mode", showLabel = true, showDescription = false, className = "" } = props;
  const { isLabs, setMe } = useMeStore();
  const { closeHandler, isLabsTabOpen } = useProjectSidebarStore();
  const [isLoading, setIsLoading] = useState(false);

  const customSnippetsUrlMutation = useMutation({
    mutationFn: updateCustomSnippetsUrl,
    onSuccess: () => {
      setMe({ customSnippetsUrl: null });
    }
  });

  const handleChange = async (checked: boolean) => {
    try {
      setIsLoading(true);
      await updateFeaturePermission(checked ? 1 : 0);
      await customSnippetsUrlMutation.mutateAsync({ customSnippetsUrl: null });
      setMe({ isLabs: checked });

      if (!checked && isLabsTabOpen()) {
        closeHandler();
      }
    } catch (error) {
      console.error(error);
      toast.error(`Failed to ${checked ? "enable" : "disable"} labs mode`);
    } finally {
      setIsLoading(false);
    }
  };

  if (variant === "inline") {
    return (
      <div className={`flex items-center justify-between ${className}`}>
        {showLabel && (
          <Label htmlFor={id} className="text-sm font-medium">
            Labs Mode
          </Label>
        )}
        <Switch id={id} checked={!!isLabs} onCheckedChange={handleChange} onClick={(e) => e.stopPropagation()} />
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-between p-4 border rounded-lg ${className}`}>
      <div className="space-y-1">
        {showLabel && (
          <Label htmlFor={id} className="text-sm font-medium">
            Labs Mode
          </Label>
        )}
        {showDescription && (
          <p className="text-sm text-muted-foreground">Enable experimental features and early access functionality</p>
        )}
      </div>
      <Switch id={id} checked={!!isLabs} disabled={isLoading} onCheckedChange={handleChange} />
    </div>
  );
}
